import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Loader2, Pencil } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useLeaveTypeDetailQuery } from "../api/queries";
import {
  LeaveTypeDetailTour,
  useLeaveTypeDetailTour,
} from "../tour/LeaveTypeDetailTour";
import { LeaveTypeForm } from "../ui/LeaveTypeForm";

const LIST_SEARCH = {
  offset: 0,
  limit: 20,
  sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
  isActive: undefined as boolean | undefined,
  search: undefined as string | undefined,
};

export function LeaveTypeDetailPage() {
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const tour = useLeaveTypeDetailTour();
  const { data: type, isLoading, error } = useLeaveTypeDetailQuery(id);

  if (isLoading || (!type && !error)) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  if (error || !type) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto w-full max-w-4xl py-4">
            <p className="text-destructive">ບໍ່ພົບປະເພດການລາ.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() =>
                nav({ to: "/app/hr/leave/types", search: LIST_SEARCH })
              }
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <LeaveTypeDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="leave-type-detail-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ລາຍລະອຽດປະເພດການລາ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{type.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                nav({ to: "/app/hr/leave/types", search: LIST_SEARCH })
              }
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບໄປລາຍການ
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                nav({
                  to: "/app/hr/leave/types/$id/edit",
                  params: { id: type.id },
                })
              }
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              ແກ້ໄຂ
            </Button>
          </div>
        </div>

        <div className="mx-auto w-full max-w-4xl py-4">
          <div data-tourid="leave-type-detail-form">
            <LeaveTypeForm
              mode="detail"
              type={type}
              onCancel={() =>
                nav({ to: "/app/hr/leave/types", search: LIST_SEARCH })
              }
            />
          </div>
        </div>
      </Main>
    </>
  );
}
